import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EASE } from '../lib/anim';

// Signature on-load moment: percentage counter + curtain wipe.
export default function Preloader() {
  const [count, setCount] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    // Skip the intro on reduced-motion, or when explicitly deep-linked.
    if (prefersReduced || params.has('skipIntro')) {
      setDone(true);
      return;
    }

    let raf;
    const start = performance.now();
    const DURATION = 1400;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / DURATION);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(Math.round(eased * 100));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setTimeout(() => setDone(true), 200);
    };
    raf = requestAnimationFrame(tick);
    document.body.style.overflow = 'hidden';
    // Hard fallbacks in case rAF/timers are throttled (background tab / headless).
    // Interval keeps advancing the count even if rAF starves; absolute cap guarantees dismissal.
    const interval = setInterval(() => {
      const p = Math.min(1, (performance.now() - start) / DURATION);
      setCount(Math.round((1 - Math.pow(1 - p, 3)) * 100));
      if (p >= 1) {
        clearInterval(interval);
        setDone(true);
      }
    }, 60);
    const fallback = setTimeout(() => {
      setCount(100);
      setDone(true);
    }, DURATION + 500);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(interval);
      clearTimeout(fallback);
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (done) document.body.style.overflow = '';
  }, [done]);

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          className="fixed inset-0 z-[100] flex flex-col justify-end bg-background px-6 pb-10 md:px-12"
          exit={{ y: '-100%' }}
          transition={{ duration: 0.9, ease: EASE }}
          data-testid="preloader"
          onClick={() => setDone(true)}
        >
          <div className="flex items-end justify-between">
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
              Michele Rago — Portfolio
            </span>
            <span className="font-display text-[18vw] font-extrabold leading-none tracking-tighter text-foreground md:text-[10vw]">
              {count}
            </span>
          </div>
          <motion.div
            className="mt-6 h-px w-full origin-left bg-foreground"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: count / 100 }}
            transition={{ ease: 'linear' }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
